import { useCallback, useEffect, useState } from 'react'

export interface SupportTicketDto {
  id: string
  kind: string
  status: string
  subject: string
  body?: string | null
  reportId?: string | null
  resolutionMessage?: string | null
  resolutionKind?: string | null
  reviewedAtUtc?: string | null
  userSeenAtUtc?: string | null
  createdAtUtc: string
}

function normalizeTicket(raw: unknown): SupportTicketDto | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const id = String(r.id ?? '')
  if (!id) return null
  return {
    id,
    kind: String(r.kind ?? ''),
    status: String(r.status ?? 'pending'),
    subject: String(r.subject ?? ''),
    body: r.body != null ? String(r.body) : null,
    reportId: r.reportId != null ? String(r.reportId) : null,
    resolutionMessage: r.resolutionMessage != null ? String(r.resolutionMessage) : null,
    resolutionKind: r.resolutionKind != null ? String(r.resolutionKind) : null,
    reviewedAtUtc: r.reviewedAtUtc != null ? String(r.reviewedAtUtc) : null,
    userSeenAtUtc: r.userSeenAtUtc != null ? String(r.userSeenAtUtc) : null,
    createdAtUtc: String(r.createdAtUtc ?? ''),
  }
}

export function usePharmacyNetworkSupport(enabled: boolean) {
  const [tickets, setTickets] = useState<SupportTicketDto[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refreshUnreadCount = useCallback(async () => {
    if (!enabled) return 0
    try {
      const res = await fetch('/api/opas/pharmacy-network/support/tickets/unread-count', {
        credentials: 'include',
        cache: 'no-store',
      })
      const data = await res.json()
      if (!res.ok) return 0
      const count = Number(data.count ?? 0)
      setUnreadCount(count)
      return count
    } catch {
      return 0
    }
  }, [enabled])

  const loadTickets = useCallback(
    async (status: 'pending' | 'resolved' = 'pending') => {
      if (!enabled) return
      setLoading(true)
      setError(null)
      try {
        const res = await fetch(
          `/api/opas/pharmacy-network/support/tickets?status=${encodeURIComponent(status)}&pageSize=50`,
          { credentials: 'include', cache: 'no-store' }
        )
        const data = await res.json()
        if (!res.ok) throw new Error(data.error ?? 'Talepler yüklenemedi')
        const items = (data.items ?? []).map(normalizeTicket).filter(Boolean) as SupportTicketDto[]
        setTickets(items)
        void refreshUnreadCount()
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Talepler yüklenemedi')
      } finally {
        setLoading(false)
      }
    },
    [enabled, refreshUnreadCount]
  )

  const createTicket = useCallback(
    async (subject: string, body: string) => {
      const res = await fetch('/api/opas/pharmacy-network/support/tickets', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body: body || null }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'Talep oluşturulamadı')
      void loadTickets('pending')
      return String(data.id ?? '')
    },
    [loadTickets]
  )

  const markSeen = useCallback(
    async (ticketId: string) => {
      const res = await fetch(`/api/opas/pharmacy-network/support/tickets/${encodeURIComponent(ticketId)}/seen`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) return
      void refreshUnreadCount()
    },
    [refreshUnreadCount]
  )

  useEffect(() => {
    if (!enabled) {
      setTickets([])
      setUnreadCount(0)
      return
    }
    void refreshUnreadCount()
  }, [enabled, refreshUnreadCount])

  return {
    tickets,
    unreadCount,
    loading,
    error,
    loadTickets,
    createTicket,
    markSeen,
    refreshUnreadCount,
  }
}
